import { createRef, VFC, useCallback } from "react";
import { TrashIcon } from "@radix-ui/react-icons";
import { NodeData, NodeInstance } from "src/types/node.types";
import { EditableNodeData } from "./editable-node-data";
import { EditableNodeName } from "./editable-node-name";
import { EditableNodeType } from "./editable-node-type";
import { FocusableNode } from "./focusable-node";
import { css } from "src/styles/theme";
import { EditorAction } from "../types/editor-action.types";
import { NodeDragger } from "./node-dragger";
import { CanvasPosition } from "./../types/canvas.types";

export interface NodeBox {
  id: string;
  width: number;
  height: number;
  x: number;
  y: number;
}

export interface EditableNodeProps {
  node: NodeInstance;
  focused: boolean;
  isActive: boolean;
  dispatch: (action: EditorAction) => void;
  position: CanvasPosition;
  scrollIntoView: (node: NodeBox) => void;
}

export const EditableNode: VFC<EditableNodeProps> = ({
  node,
  focused,
  dispatch,
  isActive,
  position,
  scrollIntoView,
}) => {
  const ref = createRef<HTMLDivElement>();

  const nodeId = node.id;
  const deleteNodeHandler = () => dispatch({ type: "DELETE_NODE", nodeId });
  const dataChangeHandler = (data: NodeData) => {
    dispatch({ type: "UPDATE_NODE", node: { ...node, data } });
  };

  const nodeChangeHandler = useCallback(
    (node: NodeInstance) => dispatch({ type: "UPDATE_NODE", node }),
    [dispatch]
  );

  const scrollIntoViewHandler = (pos: CanvasPosition = position) => {
    scrollIntoView({
      id: nodeId,
      width: ref.current!.offsetWidth,
      height: ref.current!.offsetHeight,
      x: pos.x,
      y: pos.y,
    });
  };

  const moveHandler = (pos: CanvasPosition) => {
    scrollIntoViewHandler(pos);
    dispatch({ type: "UPDATE_POSITION", id: pos.nodeId, x: pos.x, y: pos.y });
  };

  return (
    <FocusableNode
      ref={ref}
      node={node}
      focused={focused}
      dispatch={dispatch}
      className={$node({ focused, isActive })}
      scrollIntoView={scrollIntoViewHandler}
    >
      <NodeDragger
        position={position}
        onMove={moveHandler}
        scrollIntoView={scrollIntoViewHandler}
      />
      <EditableNodeType node={node} onChange={nodeChangeHandler} />
      <EditableNodeName node={node} onChange={nodeChangeHandler} />
      <EditableNodeData node={node} onChange={dataChangeHandler} />
      <button
        className={$deleteBtn()}
        aria-label="Delete Node"
        onClick={deleteNodeHandler}
      >
        <TrashIcon />
      </button>
    </FocusableNode>
  );
};

const $node = css({
  $$shadowColor: "rgba(0, 0, 0, 0.1)",

  width: "max-content",
  height: "100%",
  padding: "$2",
  display: "inline-flex",
  alignItems: "stretch",
  gap: "$3",
  borderRadius: "$2",
  boxShadow: "1px 1px 0 0 $$shadowColor",

  border: "1px solid $neutral5",
  backgroundColor: "$surface",

  "&:focus": {
    outline: 0,
  },

  variants: {
    focused: {
      true: {
        $$shadowColor: "$colors$cyanA6",
        borderColor: "$primary6",
      },
    },
    isActive: {
      false: {
        borderStyle: "dashed",
        borderColor: "$neutral5",
      },
    },
  },
});

const $deleteBtn = css({
  applyReset: "btn",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 $3",
  borderRadius: "$2",
  color: "$dangerText",

  "&:hover, &:focus": {
    outline: 0,
    backgroundColor: "$dangerBg",
  },
});
